// /home/szymon/kreator2/frontend/src/components/editor/EditorLeftToolbar.js
import React from 'react';
import { Type, Upload, Puzzle, Ruler, Users, HelpCircle, Sparkles, QrCode } from 'lucide-react'; // Dodano QrCode

/**
 * Komponent renderujący lewy pasek narzędzi edytora z ikonami pomocy.
 *
 * @param {function} panelModeForPersonalize - Funkcja sprawdzająca, czy dany tryb panelu jest aktywny (uwzględniając tryb personalizacji).
 * @param {function} handleLeftMenuClick - Funkcja obsługująca kliknięcie na ikonę w toolbarze.
 * @param {boolean} hasPersonalize - Flaga wskazująca, czy projekt ma elementy do personalizacji.
 * @param {boolean} tooltipsEnabled - Flaga wskazująca, czy tooltipy są włączone.
 * @param {boolean} publicMode - Flaga wskazująca, czy edytor jest w trybie publicznym.
 * @param {function} onHelpClick - Funkcja do obsługi kliknięcia ikony pomocy.
 */
export default function EditorLeftToolbar({
  panelModeForPersonalize,
  handleLeftMenuClick,
  hasPersonalize,
  tooltipsEnabled, // Odbieramy nowy prop
  isPersonalizedProject, // Odbieramy flagę
  publicMode, // Dodajemy prop publicMode
  onHelpClick // Dodajemy prop onHelpClick
}) {
  // Funkcja pomocnicza do generowania propsów tooltipa
  const tooltipProps = (content) => {
    return tooltipsEnabled ? { "data-tooltip-id": "editor-tooltip", "data-tooltip-content": content } : {};
  };

  return (
    <div
      style={{
        width: 70,
        flex: "0 0 auto",
        backgroundColor: "#f0f0f0",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        paddingTop: 10,
        gap: 15 // Dodajemy odstęp między ikonami
      }}
    >
      {/* Ikona narzędzia Tekst z pomocą */}
      <div
        style={{
          backgroundColor: panelModeForPersonalize("text") ? "#e2e8f0" : "transparent", // Lepsze tło aktywne (Tailwind gray-200)
          width: "100%",
          padding: "8px 0", // Zwiększony padding
          textAlign: "center",
          cursor: "pointer",
          borderRadius: '4px', // Zaokrąglenie
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '5px', // Odstęp między ikoną a pomocą
          position: 'relative' // Dla pozycjonowania ikony pomocy
        }}
        onClick={() => handleLeftMenuClick("text")}
        {...tooltipProps("Narzędzie Tekst: Kliknij, aby dodać nowy tekst do projektu lub edytować istniejący.")}
      >
        <Type size={32} color={panelModeForPersonalize("text") ? "#2d3748" : "#4a5568"} /> {/* Lucide icon */}
        {tooltipsEnabled && <HelpCircle
          size={14}
          color="#a0aec0" // Tailwind gray-400
          style={{ position: 'absolute', top: 2, right: 2, cursor: 'help' }}
          {...tooltipProps("Dodaj nowe pole tekstowe do projektu.")}
        />}
      </div>

      {/* Ikona narzędzia Upload z pomocą */}
      <div
        style={{
          backgroundColor: panelModeForPersonalize("upload") ? "#e2e8f0" : "transparent",
          width: "100%",
          padding: "8px 0",
          textAlign: "center",
          cursor: "pointer",
          borderRadius: '4px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative'
        }}
        onClick={() => handleLeftMenuClick("upload")}
        {...tooltipProps("Narzędzie Upload: Kliknij, aby wgrać własne pliki graficzne (np. zdjęcia, logo).")}
      >
        <Upload size={32} color={panelModeForPersonalize("upload") ? "#2d3748" : "#4a5568"} /> {/* Lucide icon */}
        {tooltipsEnabled && <HelpCircle
          size={14}
          color="#a0aec0"
          style={{ position: 'absolute', top: 2, right: 2, cursor: 'help' }}
          {...tooltipProps("Wgraj własne pliki graficzne (JPG, PNG, SVG).")}
        />}
      </div>

      {/* Ikona narzędzia Rebusy z pomocą */}
      <div
        style={{
          backgroundColor: panelModeForPersonalize("rebuses") ? "#e2e8f0" : "transparent",
          width: "100%",
          padding: "8px 0",
          textAlign: "center",
          cursor: "pointer",
          borderRadius: '4px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative'
        }}
        onClick={() => handleLeftMenuClick("rebuses")}
        {...tooltipProps("Narzędzie Rebusy: Kliknij, aby przeglądać i dodawać gotowe grafiki (rebusy) do projektu.")}
      >
        <Puzzle size={32} color={panelModeForPersonalize("rebuses") ? "#2d3748" : "#4a5568"} /> {/* Lucide icon */}
         {tooltipsEnabled && <HelpCircle
          size={14}
          color="#a0aec0"
          style={{ position: 'absolute', top: 2, right: 2, cursor: 'help' }}
          {...tooltipProps("Wybierz i dodaj gotowe elementy graficzne z biblioteki.")}
        />}
      </div>

      {/* Ikona narzędzia Linie Pomocnicze z pomocą */}
      <div
        style={{
          backgroundColor: panelModeForPersonalize("guides") ? "#e2e8f0" : "transparent",
          width: "100%",
          padding: "8px 0",
          textAlign: "center",
          cursor: "pointer",
          borderRadius: '4px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative'
        }}
         onClick={() => handleLeftMenuClick("guides")}
         {...tooltipProps("Narzędzie Linie Pomocnicze: Kliknij, aby zarządzać liniami pomocniczymi ułatwiającymi wyrównywanie elementów.")}
      >
        <Ruler size={32} color={panelModeForPersonalize("guides") ? "#2d3748" : "#4a5568"} /> {/* Lucide icon */}
         {tooltipsEnabled && <HelpCircle
          size={14}
          color="#a0aec0"
          style={{ position: 'absolute', top: 2, right: 2, cursor: 'help' }}
          {...tooltipProps("Dodawaj, usuwaj i modyfikuj linie pomocnicze (prowadnice).")}
        />}
      </div>

      {/* Ikona narzędzia Blissaro QR (dostępna w trybie publicznym i admina) */}
      <div
          style={{
            backgroundColor: panelModeForPersonalize("blissaro") ? "#e2e8f0" : "transparent",
            width: "100%",
            padding: "8px 0",
            textAlign: "center",
            cursor: "pointer",
            borderRadius: '4px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative'
          }}
          onClick={() => handleLeftMenuClick("blissaro")}
          {...tooltipProps("Blissaro QR: Generuj kody QR dla gości weselnych do potwierdzania obecności.")}
        >
          <QrCode size={32} color={panelModeForPersonalize("blissaro") ? "#2d3748" : "#4a5568"} />
          {tooltipsEnabled && <HelpCircle
            size={14}
            color="#a0aec0"
            style={{ position: 'absolute', top: 2, right: 2, cursor: 'help' }}
            {...tooltipProps("Integracja z aplikacją Blissaro do zarządzania obecnością gości.")}
          />}
        </div>

      {/* Ikona narzędzia Personalizacja (warunkowo - tylko jeśli projekt ma pola do personalizacji) z pomocą */}
      {hasPersonalize && (
        <div
          style={{
            backgroundColor: panelModeForPersonalize("personalize") ? "#e2e8f0" : "transparent",
            width: "100%",
            padding: "8px 0",
            textAlign: "center",
            cursor: "pointer",
            borderRadius: '4px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            // marginTop: 'auto', // Usunięto, aby ikona była pod innymi
            marginBottom: 10 // Zachowujemy margines dolny
          }}
           onClick={() => handleLeftMenuClick("personalize")}
           {...tooltipProps("Narzędzie Personalizacja: Kliknij, aby edytować listę gości lub inne dane personalizacyjne.")}
        >
          <Users size={32} color={panelModeForPersonalize("personalize") ? "#2d3748" : "#4a5568"} /> {/* Lucide icon */}
           {tooltipsEnabled && <HelpCircle
            size={14}
            color="#a0aec0"
            style={{ position: 'absolute', top: 2, right: 2, cursor: 'help' }}
            {...tooltipProps("Wprowadź listę gości lub inne dane do automatycznego uzupełnienia w projekcie.")}
          />}
        </div>
      )}

      {/* Nowa ikona "Wsparcie Projektowe" (tylko w trybie publicznym) */}
      {publicMode && (
      <div
        style={{
          backgroundColor: panelModeForPersonalize("projectSupport") ? "#e2e8f0" : "transparent",
          width: "100%",
          padding: "8px 0",
          textAlign: "center",
          cursor: "pointer",
          borderRadius: '4px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          // marginTop: 'auto', // Usunięto, aby była przed ikoną Pomocy
        }}
        onClick={() => handleLeftMenuClick("projectSupport")}
        {...tooltipProps("Wsparcie Projektowe: Poproś o pomoc grafika lub AI.")}
      >
        <Sparkles size={32} color={panelModeForPersonalize("projectSupport") ? "#2d3748" : "#4a5568"} />
        {tooltipsEnabled && <HelpCircle
          size={14}
          color="#a0aec0"
          style={{ position: 'absolute', top: 2, right: 2, cursor: 'help' }}
          {...tooltipProps("Poproś grafika o zmiany lub pozwól AI ulepszyć projekt.")}
        />}
      </div>
      )}

      {/* Ikona Pomocy (tylko w trybie publicznym) */}
      {publicMode && (
        <div
          style={{
            backgroundColor: 'transparent', // Nieaktywna
            width: "100%",
            padding: "8px 0",
            textAlign: "center",
            cursor: "pointer",
            borderRadius: '4px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            marginTop: 'auto', // Wypchnięcie na dół
            marginBottom: 10
          }}
           onClick={onHelpClick} // Używamy nowego handlera
           {...tooltipProps("Pomoc: Kliknij, aby otworzyć instrukcję obsługi kreatora.")}
        >
          <HelpCircle size={32} color={"#4a5568"} /> {/* Ikona pomocy */}
        </div>
      )}
    </div>
  );
}
